import React, { PropsWithChildren, useMemo } from 'react';
import { Field, useFormState } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { useSelector } from 'react-redux';

import {
  EmailRecipientsArray,
  FieldCheckbox,
  FieldTextInput,
  SignersArray,
} from 'Components/FormFields';
import { DocumentValues, InteractExtraValues } from 'Interfaces/Document';
import { User } from 'Interfaces/User';
import { composeValidators } from 'Utils/functions';
import { selectUser } from 'Utils/selectors';
import { email, notOnlySpaces, required } from 'Utils/validation';

interface SelectActionFormSignAndSendPageProps {
  isChooseActionDisabled?: boolean;
  onSetExtraValues: (values: InteractExtraValues) => void;
}

const SelectActionFormSignAndSendPage = ({
  children,
  isChooseActionDisabled,
  onSetExtraValues,
}: PropsWithChildren<SelectActionFormSignAndSendPageProps>) => {
  const { name: userName }: User = useSelector(selectUser);
  const { values } = useFormState<DocumentValues>();

  const firstSignerIndex = useMemo(() => {
    const signersOrders = values?.signers
      ?.map(signer => signer?.order)
      .filter(order => order !== undefined) as number[];
    const minimalOrder = Math.min(...signersOrders);
    const signerIndex = values?.signers?.findIndex(
      signer => signer?.order === minimalOrder,
    );
    return signerIndex && signerIndex !== -1 ? signerIndex : 0;
  }, [values]);

  return (
    <>
      <div className="selectActionForm__emailField">
        <p className="selectActionForm__emailField-title">Signers</p>
        <div className="signers__item vertical signers__item-inner signers__item--me">
          <Field
            name={`signers[${firstSignerIndex}].name`}
            label="Me"
            component={FieldTextInput}
            format={value => (value ? `Me (${userName})` : 'Me')}
            disabled
          />
          <Field
            name={`signers[${firstSignerIndex}].email`}
            component={FieldTextInput}
            disabled
          />
        </div>
      </div>
      <p className="selectActionForm__emailField-title">Add Other Signers</p>
      <div className="selectActionForm__signersContainer">
        <FieldArray
          name="signers"
          render={props => (
            <SignersArray
              {...props}
              isAdditionDisabled={isChooseActionDisabled}
              isRemoveDisabled={isChooseActionDisabled}
              isItemDeletablePredicate={({ fields }) =>
                !!fields.length && fields.length > 2
              }
              handleSetSignersValues={onSetExtraValues}
              renderFields={name => (
                <div className="selectActionForm__signerItem">
                  <Field
                    name={`${name}.name`}
                    placeholder="Name"
                    component={FieldTextInput}
                    validate={composeValidators<string>(required, notOnlySpaces)}
                    disabled={isChooseActionDisabled}
                  />
                  <Field
                    name={`${name}.email`}
                    placeholder="Email"
                    component={FieldTextInput}
                    validate={composeValidators<string>(required, email)}
                    disabled={isChooseActionDisabled}
                  />
                </div>
              )}
            />
          )}
          isOrdered={values.isOrdered}
        />
      </div>
      <Field
        name="isOrdered"
        label="Custom signing order"
        className="selectActionForm__emailField-isOrdered"
        render={FieldCheckbox}
        onClick={onSetExtraValues}
      />
      <div className="selectActionForm__emailField">
        <p className="selectActionForm__emailField-title selectActionForm__emailField-title--expanded">
          Add Viewers
        </p>
        <FieldArray
          name="recipients"
          label={null}
          component={EmailRecipientsArray}
          isItemsDeletable
          handleSetRecipientsValues={onSetExtraValues}
        />
      </div>
      {children}
    </>
  );
};

export default SelectActionFormSignAndSendPage;
